'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  Share2,
  MousePointerClick,
  Search,
  Globe,
  Palette,
  Briefcase,
  ChevronDown,
  ArrowRight,
  CheckCircle2,
  ArrowUpRight,
} from 'lucide-react';
import CtaLabel, { CTA_ASSESSMENT, CTA_BOOKING } from '@/components/ui/CtaLabel';
import Carousel, { type CarouselHandle, type CarouselSlide } from './Carousel';
import { SECTION_HEADING_CLASS, SECTION_HEADING_STYLE } from './sectionHeading';

const services = [
  {
    id: 'svc-social',
    slug: 'social-media-digital-marketing',
    icon: Share2,
    number: '01',
    title: 'Social Media & Digital Marketing',
    tagline: 'Build an audience that buys',
    problem: 'Posting without a strategy and seeing little engagement or sales.',
    outcome:
      'A consistent content system that drives brand awareness and converts followers into customers.',
    description:
      'We craft engaging social media content and strategies tailored to your audience. From content calendars to community management, we handle everything that turns your social presence into a revenue channel.',
    deliverables: [
      'Content calendar (30 days)',
      'Platform strategy (Instagram, LinkedIn, TikTok)',
      'Monthly performance reports',
      'Audience growth plan',
      'Digital product creation (guides, e-books)',
    ],
  },
  {
    id: 'svc-ads',
    slug: 'paid-ads-management',
    icon: MousePointerClick,
    number: '02',
    title: 'Paid Ads Management',
    tagline: 'Get in front of ready-to-buy customers',
    problem: "Wasting budget on ads that don't convert or reach the wrong audience.",
    outcome: 'Predictable, measurable ROI from Facebook, Instagram, and Google Ads campaigns.',
    description:
      'Expertly managed paid advertising campaigns designed to drive targeted traffic and qualified leads. We handle audience targeting, creative testing, bid optimization, and provide detailed monthly analytics.',
    deliverables: [
      'Facebook & Instagram Ads setup',
      'Google Search & Display Ads',
      'Audience targeting strategy',
      'A/B creative testing',
      'Monthly analytics reports',
    ],
  },
  {
    id: 'svc-seo',
    slug: 'seo-content-creation',
    icon: Search,
    number: '03',
    title: 'SEO & Content Creation',
    tagline: 'Grow traffic that compounds over time',
    problem: 'Invisible on Google while competitors capture your potential customers.',
    outcome:
      'A steady stream of organic traffic that grows month after month without ongoing ad spend.',
    description:
      'Comprehensive SEO strategies combined with professionally written, optimized content. We fix technical issues, create helpful content, and earn authoritative links to keep your site ranking.',
    deliverables: [
      'Technical SEO audit & fixes',
      'Keyword research & strategy',
      'Monthly blog articles',
      'On-page optimization',
      'Link building outreach',
    ],
  },
  {
    id: 'svc-website',
    slug: 'website-creation',
    icon: Globe,
    number: '04',
    title: 'Website Creation',
    tagline: 'A site that works as hard as you do',
    problem: 'An outdated, slow, or hard-to-update website that loses you customers.',
    outcome:
      'A sleek, mobile-ready website or e-commerce store you can manage yourself — no coding required.',
    description:
      'We build conversion-optimized websites and online stores with drag-and-drop editing, secure payments, and built-in analytics. Fast, accessible, and designed to grow with your business.',
    deliverables: [
      'Custom website design',
      'Mobile-first development',
      'E-commerce integration',
      'CMS setup (self-editable)',
      'Speed & performance optimization',
    ],
  },
  {
    id: 'svc-design',
    slug: 'digital-design',
    icon: Palette,
    number: '05',
    title: 'Digital Design',
    tagline: 'Visuals that tell your story clearly',
    problem: 'Inconsistent branding that makes you look unprofessional or forgettable.',
    outcome: 'A cohesive visual identity with stand-out assets ready for every screen and channel.',
    description:
      'Stand-out visuals for every platform. We craft logos, banners, social posts, and UI mock-ups that communicate your brand clearly. You receive organized, editable templates your team can update in minutes.',
    deliverables: [
      'Logo & brand identity',
      'Social media templates',
      'Banners & ad creatives',
      'UI mock-ups',
      'Brand style guide',
    ],
  },
  {
    id: 'svc-consulting',
    slug: 'business-consulting',
    icon: Briefcase,
    number: '06',
    title: 'Business Consulting',
    tagline: 'Clarity on what to do next',
    problem:
      'Feeling stuck, overwhelmed, or unsure which lever to pull to move the business forward.',
    outcome: 'Clear, actionable steps that turn ambitious goals into measurable business wins.',
    description:
      'Our advisors analyze your data, processes, and market to uncover hidden growth opportunities. From pricing and product-market fit to operational efficiency, we deliver a strategic roadmap with prioritized next steps.',
    deliverables: [
      'Business diagnostic session',
      'Growth opportunity analysis',
      'Strategic roadmap (90-day)',
      'Mentorship & accountability',
      'Startup program access',
    ],
  },
];

export default function ServicesSection() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  /* The row itself is a shared component; this section only has to hold on to
     it so a recommendation from the diagnostic can be carried in. */
  const carouselRef = useRef<CarouselHandle | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const placeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Moving the row is pointless if the row is off-screen, and jumping the page
     when it is already being read is worse. So: scroll vertically only when the
     cards are not substantially in view, and land them below the fixed header
     rather than under it.

     The scroll then has to be confirmed, because two other things on this page
     write to the same scroll position. framer-motion re-measures the document
     when the deliverables panel begins its height animation and restores the
     scroll as it does; a smooth-scrolling library, where one is installed, holds
     the page at its own target and swallows a native scroll it did not issue.
     Both leave the page sitting exactly where it started. Waiting a fixed delay
     to clear them is a guess that has to be re-tuned every time the page gains
     an animation, so instead: ask, then check whether the page actually moved,
     and ask again if it did not. A page still travelling is left alone, and so
     is a visitor who has taken over the scroll themselves. */
  const placeSection = useCallback(() => {
    const viewport = carouselRef.current?.element();
    if (!viewport || typeof window === 'undefined') return;
    const rect = viewport.getBoundingClientRect();
    const header = document.querySelector('header');
    const top = (header ? header.getBoundingClientRect().height : 64) + 16;
    const room = window.innerHeight - top;
    const shown = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, top);
    if (shown >= Math.min(rect.height, room * 0.6)) return;

    const target = Math.max(0, Math.round(window.scrollY + rect.top - top));
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const scroll = () => window.scrollTo({ top: target, behavior });

    let previous = window.scrollY;
    let attempts = 0;
    const stop = () => {
      if (confirmTimer.current) {
        clearTimeout(confirmTimer.current);
        confirmTimer.current = null;
      }
      ['wheel', 'touchstart', 'keydown'].forEach((type) => window.removeEventListener(type, stop));
    };
    const confirm = () => {
      const y = window.scrollY;
      // Arrived, or the visitor is scrolling somewhere of their own accord.
      if (Math.abs(y - target) <= 4 || attempts >= 3) return stop();
      attempts += 1;
      // Still travelling — leave the scroll that is already running alone.
      if (Math.abs(y - previous) <= 2) scroll();
      previous = y;
      confirmTimer.current = setTimeout(confirm, 150);
    };

    stop();
    scroll();
    ['wheel', 'touchstart', 'keydown'].forEach((type) =>
      window.addEventListener(type, stop, { once: true, passive: true })
    );
    confirmTimer.current = setTimeout(confirm, 150);
  }, []);

  /* The diagnostic in the challenges section above still decides which service
     answers which problem; this only carries its answer into the row. Marking
     the event handled tells it not to scroll the card into view itself, which
     would be a second scroll competing with this one. */
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        { serviceId?: string; handled?: boolean } | undefined;
      const serviceId = detail?.serviceId;
      const index = services.findIndex((s) => s.id === serviceId);
      if (!serviceId || index < 0) return;
      if (detail) detail.handled = true;

      setHighlighted(serviceId);
      setExpanded(serviceId);
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlighted(null), 3000);

      /* Deferred a tick: the card is opening its deliverables, and a
         measurement taken now would be of the closed one. Re-arming the timer,
         rather than adding one, is what lets a second choice overtake a first
         that is still in flight. */
      if (placeTimer.current) clearTimeout(placeTimer.current);
      placeTimer.current = setTimeout(() => {
        carouselRef.current?.reveal(index);
        placeSection();
      }, 90);
    };
    window.addEventListener('highlight-service', handler);
    return () => {
      window.removeEventListener('highlight-service', handler);
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      if (placeTimer.current) clearTimeout(placeTimer.current);
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    };
  }, [placeSection]);

  const toggle = (id: string) => setExpanded((current) => (current === id ? null : id));

  /* The whole tile opens the card, not just the small toggle at the bottom of
     it. The toggle stays a real <button> underneath: it is what a keyboard and
     a screen reader use, and it is what carries the accessible name and the
     expanded state. This handler only adds the mouse convenience on top, so
     the tile never becomes an interactive element with no name.

     A click that ends a text selection is ignored — otherwise highlighting a
     line of the description would snap the card shut. */
  const toggleFromTile = (id: string) => {
    if (typeof window !== 'undefined' && window.getSelection()?.toString()) return;
    toggle(id);
  };

  return (
    <section
      data-ground
      id="services"
      className="section-rhythm"
      ref={ref}
      style={{
        background:
          'linear-gradient(180deg, transparent 0%, var(--m-panel) 7%, var(--m-panel) 93%, transparent 100%)',
      }}
    >
      <div className="m-container">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-16"
        >
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div style={{ width: 24, height: 1, background: 'rgba(var(--m-ink-rgb),0.3)' }} />
                <span
                  className="font-mono text-[11px] uppercase tracking-widest"
                  style={{ color: 'var(--m-muted)', letterSpacing: '0.22em' }}
                >
                  SVC — 01 through 06
                </span>
              </div>
              <h2 className={SECTION_HEADING_CLASS} style={SECTION_HEADING_STYLE}>
                {/* A phone shows one heading at a time, so it has to say what
                    is below it rather than make a claim. */}
                <span className="sm:hidden">Six ways we help you grow</span>
                <span className="hidden sm:inline">
                  Everything your
                  <br />
                  business needs
                  <br />
                  to grow online
                </span>
              </h2>
            </div>
            <div style={{ maxWidth: 320 }}>
              {/* The section asked for the diagnostic twice — here and again in
                  the strip below the cards, both pointing at the same page. Two
                  asks for one thing is not twice the invitation; it just makes
                  the section look unsure of itself. The strip keeps it, because
                  that is where a reader who has been through the six services
                  and not found their answer actually is. */}
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--m-ink-6)', lineHeight: 1.7 }}
              >
                From paid ads to strategic consulting — we combine marketing execution with business
                strategy so every campaign moves the whole company forward.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Service carousel — one row of six, three to a frame on a desktop */}
        <Carousel
          ref={carouselRef}
          id="services-carousel"
          label="Our services"
          noun="Service"
          isInView={isInView}
          slides={services.map((service): CarouselSlide => {
            const isHighlighted = highlighted === service.id;
            const isExpanded = expanded === service.id;
            return {
              key: service.id,
              // Outside code scrolls to this: the challenges section falls back
              // to it when nothing answers its recommendation event.
              id: `service-card-${service.id}`,
              title: service.title,
              number: service.number,
              cardProps: {
                className: 'relative group',
                onClick: () => toggleFromTile(service.id),
                style: {
                  background: isHighlighted ? 'var(--m-surface-2)' : 'var(--m-bg)',
                  /* Raised off the ground rather than drawn on it. The selected
                     card comes further forward, which is most of what "selected"
                     means to an eye. */
                  boxShadow: isHighlighted
                    ? 'var(--m-lift-2), var(--m-lift-edge)'
                    : 'var(--m-lift-1), var(--m-lift-edge)',
                  transition:
                    'background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease',
                  borderColor: isHighlighted
                    ? 'rgba(var(--m-ink-rgb),0.32)'
                    : 'rgba(var(--m-ink-rgb),0.13)',
                  cursor: 'pointer',
                },
                onMouseEnter: (e) => {
                  if (!isHighlighted) {
                    const card = e.currentTarget as HTMLElement;
                    card.style.background = 'var(--m-surface-2)';
                    card.style.borderColor = 'rgba(var(--m-ink-rgb),0.26)';
                  }
                },
                onMouseLeave: (e) => {
                  if (!isHighlighted) {
                    const card = e.currentTarget as HTMLElement;
                    card.style.background = 'var(--m-bg)';
                    card.style.borderColor = 'rgba(var(--m-ink-rgb),0.13)';
                  }
                },
              },
              content: (
                <>
                  {/* Highlight pulse */}
                  <AnimatePresence>
                    {isHighlighted && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 0] }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 2.5 }}
                        className="absolute inset-0 pointer-events-none z-10"
                        style={{ boxShadow: 'inset 0 0 0 1px rgba(var(--m-ink-rgb),0.2)' }}
                      />
                    )}
                  </AnimatePresence>

                  <div className="p-7 flex flex-col h-full">
                    {/* Number + icon row */}
                    <div className="flex items-start justify-between mb-6">
                      <span
                        className="font-mono font-bold"
                        style={{
                          fontSize: 'clamp(36px, 4vw, 52px)',
                          color: 'rgba(var(--m-ink-rgb),0.07)',
                          lineHeight: 1,
                          letterSpacing: '-0.02em',
                          fontFamily: "'Archivo Narrow', sans-serif",
                        }}
                      >
                        {service?.number}
                      </span>
                      <div
                        className="flex items-center justify-center flex-shrink-0 transition-all duration-200"
                        style={{
                          width: 40,
                          height: 40,
                          border: '1px solid rgba(var(--m-ink-rgb),0.12)',
                          background: 'var(--m-bg)',
                        }}
                      >
                        <service.icon
                          className="w-4.5 h-4.5"
                          style={{ color: 'var(--m-ink-3)', width: 18, height: 18 }}
                        />
                      </div>
                    </div>

                    {/* Title + tagline */}
                    <h3
                      className="font-bold uppercase mb-1 leading-tight"
                      style={{
                        fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                        fontSize: 'clamp(15px, 1.4vw, 18px)',
                        color: 'var(--m-ink)',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {service?.title}
                    </h3>
                    <p
                      className="font-mono uppercase mb-4"
                      style={{ fontSize: 11, color: 'var(--m-ink-6)', letterSpacing: '0.12em' }}
                    >
                      {service?.tagline}
                    </p>

                    {/* Thin divider */}
                    <div
                      style={{
                        height: 1,
                        background: 'rgba(var(--m-ink-rgb),0.08)',
                        marginBottom: 16,
                      }}
                    />

                    {/* On a phone the card is cut to the two lines that actually
                      help someone choose: where they are now, and where this
                      service gets them. The prose description moves behind the
                      toggle below rather than being deleted. Desktop, which has
                      room for all four layers side by side, is unchanged. */}
                    <dl className="grid gap-2.5 sm:hidden mb-4">
                      <div>
                        <dt
                          className="font-mono uppercase"
                          style={{
                            fontSize: 11,
                            letterSpacing: '0.12em',
                            color: 'var(--m-ink-6)',
                            marginBottom: 2,
                          }}
                        >
                          Now
                        </dt>
                        <dd style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--m-ink-5)' }}>
                          {service?.problem}
                        </dd>
                      </div>
                      <div>
                        <dt
                          className="font-mono uppercase"
                          style={{
                            fontSize: 11,
                            letterSpacing: '0.12em',
                            color: 'var(--m-ink-6)',
                            marginBottom: 2,
                          }}
                        >
                          After
                        </dt>
                        <dd
                          style={{
                            fontSize: 14,
                            lineHeight: 1.5,
                            color: 'var(--m-ink-2)',
                            fontWeight: 500,
                          }}
                        >
                          {service?.outcome}
                        </dd>
                      </div>
                    </dl>

                    {/* Description */}
                    <p
                      className="hidden sm:block text-sm leading-relaxed flex-1 mb-5"
                      style={{ color: 'var(--m-ink-5)', lineHeight: 1.65 }}
                    >
                      {service?.description}
                    </p>

                    {/* Expand toggle */}
                    <button
                      onClick={(e) => {
                        // The tile behind this handles the same click, so without
                        // this the card would open and immediately close again.
                        e.stopPropagation();
                        toggle(service.id);
                      }}
                      aria-expanded={isExpanded}
                      aria-controls={`service-deliverables-${service.id}`}
                      className="flex items-center gap-1.5 font-mono uppercase tracking-widest transition-colors self-start min-h-[44px] sm:min-h-[28px]"
                      style={{ color: 'var(--m-muted)', letterSpacing: '0.12em', fontSize: 9 }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.color = 'var(--m-ink-2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.color = 'var(--m-muted)';
                      }}
                    >
                      {isExpanded ? (
                        'Collapse'
                      ) : (
                        <CtaLabel mobile="See what's included" desktop="See deliverables" />
                      )}
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </motion.div>
                    </button>

                    {/* Expandable deliverables */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          id={`service-deliverables-${service.id}`}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                          /* Once open, this panel is for reading and for the link
                           at the end of it. Collapsing the card out from under
                           a click aimed at either would be the wrong answer. */
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="mt-4 pt-4"
                            style={{ borderTop: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
                          >
                            {/* The prose the phone's collapsed card leaves out.
                              Hidden on desktop, where it is already shown above. */}
                            <p
                              className="sm:hidden mb-4"
                              style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--m-ink-5)' }}
                            >
                              {service?.description}
                            </p>
                            <p
                              className="font-mono uppercase tracking-widest mb-3"
                              style={{
                                fontSize: 11,
                                color: 'var(--m-muted)',
                                letterSpacing: '0.14em',
                              }}
                            >
                              Key Deliverables
                            </p>
                            <ul className="space-y-2 mb-4">
                              {service?.deliverables?.map((d) => (
                                <li
                                  key={`del-${service?.id}-${d?.slice(0, 15)}`}
                                  className="flex items-start gap-2 text-sm"
                                  style={{ color: 'var(--m-ink-5)' }}
                                >
                                  <CheckCircle2
                                    className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                                    style={{ color: 'var(--m-muted)' }}
                                  />
                                  {d}
                                </li>
                              ))}
                            </ul>
                            <Link
                              href={`/services/${service?.slug}`}
                              className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest transition-colors"
                              style={{
                                color: 'var(--m-ink-3)',
                                letterSpacing: '0.12em',
                                fontSize: 11,
                                textDecoration: 'none',
                                borderBottom: '1px solid rgba(var(--m-ink-rgb),0.2)',
                                paddingBottom: 1,
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLElement).style.color = 'var(--m-ink)';
                                (e.currentTarget as HTMLElement).style.borderBottomColor =
                                  'var(--m-ink)';
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLElement).style.color = 'var(--m-ink-3)';
                                (e.currentTarget as HTMLElement).style.borderBottomColor =
                                  'rgba(var(--m-ink-rgb),0.2)';
                              }}
                            >
                              View full service details <ArrowRight className="w-3 h-3" />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              ),
            };
          })}
        />

        {/* Bottom CTA strip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="mt-px flex flex-col sm:flex-row items-stretch"
          style={{ background: 'rgba(var(--m-ink-rgb),0.08)' }}
        >
          <div
            className="flex-1 flex items-center justify-between px-7 py-5"
            style={{ background: 'var(--m-ink)' }}
          >
            <p
              className="font-mono uppercase text-[11px] tracking-widest"
              style={{ color: 'rgba(var(--m-on-ink-rgb),0.5)', letterSpacing: '0.18em' }}
            >
              Not sure which service fits?
            </p>
            <Link
              href="/business-assessment"
              className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-widest transition-opacity hover:opacity-80"
              style={{
                color: 'var(--m-on-ink)',
                textDecoration: 'none',
                letterSpacing: '0.14em',
                flexShrink: 0,
                marginLeft: 16,
                minHeight: 32,
              }}
            >
              <CtaLabel mobile={CTA_ASSESSMENT} desktop="Run the diagnostic" />{' '}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div
            className="flex items-center justify-between px-7 py-5 sm:w-64"
            style={{
              background: 'var(--m-surface-2)',
              borderLeft: '1px solid rgba(var(--m-ink-rgb),0.08)',
            }}
          >
            <p
              className="font-mono uppercase text-[11px] tracking-widest"
              style={{ color: 'var(--m-muted)', letterSpacing: '0.14em' }}
            >
              Ready to start?
            </p>
            <Link
              href="/book-a-consultation"
              className="inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-widest transition-colors"
              style={{
                color: 'var(--m-ink)',
                textDecoration: 'none',
                letterSpacing: '0.14em',
                borderBottom: '1px solid rgba(var(--m-ink-rgb),0.3)',
                paddingBottom: 2,
                minHeight: 32,
                flexShrink: 0,
                marginLeft: 12,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderBottomColor = 'var(--m-ink)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderBottomColor =
                  'rgba(var(--m-ink-rgb),0.3)';
              }}
            >
              <CtaLabel mobile={CTA_BOOKING} desktop="Book a call" />{' '}
              <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
